/**
 * DRBFM Table Component
 *
 * Displays DRBFM analysis data in a tabular format.
 * Supports three data types:
 * - Design Changes
 * - Failure Modes
 * - Countermeasures
 */

import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  ColumnDef,
  PaginationState,
  getCoreRowModel,
  getPaginationRowModel,
  useReactTable,
} from '@tanstack/react-table';
import {
  Table,
  TableBody,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { DataTablePagination } from '@/components/ui/data-table-pagination';
import { TABLE_DEFAULT_PAGE_SIZE } from '@constants/table';
import {
  DesignChange,
  FailureMode,
  Countermeasure,
  UpdateDesignChangeReviewRequest,
} from '../../types';
import CountermeasureRow from './table/CountermeasureRow';
import DesignChangeRow from './table/DesignChangeRow';
import FailureModeRow from './table/FailureModeRow';

// Type definitions for table data
type TableDataType = 'designChanges' | 'failureModes' | 'countermeasures';

type DRBFMRow = DesignChange | FailureMode | Countermeasure;

interface DRBFMTableProps {
  type: TableDataType;
  data: DesignChange[] | FailureMode[] | Countermeasure[];
  /**
   * DRBFM session id used for the 3D navigation. When omitted the
   * 3D action column is hidden for failure modes (TG #673).
   */
  drbfmSessionId?: string;
  selectedFailureModeId?: string;
  onSelectFailureMode?: (failureMode: FailureMode) => void;
  reviewingArtifactId?: string;
  onReviewDesignChange?: (
    designChangeId: string,
    request: UpdateDesignChangeReviewRequest,
  ) => Promise<void>;
}

const tableMinWidthByType: Record<TableDataType, string> = {
  designChanges: 'min-w-[820px]',
  failureModes: 'min-w-[1120px] table-fixed',
  countermeasures: 'min-w-[720px]',
};

const PAGE_SIZES = [5, 10, 25, 50, 100];

/**
 * Rows are rendered by hand rather than through ui/data-table, because two of
 * the three shapes expand into a full-width detail row that the shared table
 * cannot express. The instance below therefore carries no columns: it exists
 * only to own the paging state that DataTablePagination reads.
 */
const NO_COLUMNS: ColumnDef<DRBFMRow>[] = [];

const DRBFMTable: React.FC<DRBFMTableProps> = ({
  type,
  data,
  drbfmSessionId,
  selectedFailureModeId,
  onSelectFailureMode,
  reviewingArtifactId,
  onReviewDesignChange,
}) => {
  const { t } = useTranslation();
  const [pagination, setPagination] = useState<PaginationState>({
    pageIndex: 0,
    pageSize: TABLE_DEFAULT_PAGE_SIZE,
  });

  const table = useReactTable<DRBFMRow>({
    data: data as DRBFMRow[],
    columns: NO_COLUMNS,
    state: { pagination },
    onPaginationChange: setPagination,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
  });

  const paginatedData = table.getRowModel().rows.map((row) => row.original);

  // Empty state
  if (data.length === 0) {
    return (
      <div className="py-8 text-center">
        <p className="revy-cjk text-sm leading-[1.5] text-muted-foreground">
          {t('drbfm.table.noData')}
        </p>
      </div>
    );
  }

  // Render table based on type
  const renderTable = () => {
    switch (type) {
      case 'designChanges':
        return (
          <Table className={tableMinWidthByType.designChanges}>
            <TableHeader>
              <TableRow className="hover:bg-transparent">
                <TableHead className="w-[50px]" />
                <TableHead className="revy-cjk">{t('drbfm.table.category')}</TableHead>
                <TableHead className="revy-cjk">{t('drbfm.table.itemName')}</TableHead>
                <TableHead className="revy-cjk">{t('drbfm.table.changeDescription')}</TableHead>
                <TableHead className="revy-cjk">{t('drbfm.table.impactAreas')}</TableHead>
                {onReviewDesignChange && (
                  <TableHead className="revy-cjk text-right">
                    {t('drbfm.decisionMemory.humanDecisionTitle', '設計者の判断')}
                  </TableHead>
                )}
              </TableRow>
            </TableHeader>
            <TableBody>
              {paginatedData.map((row) => (
                <DesignChangeRow
                  key={(row as DesignChange).id}
                  row={row as DesignChange}
                  isBusy={Boolean(reviewingArtifactId)}
                  onReview={onReviewDesignChange
                    ? (request) => onReviewDesignChange((row as DesignChange).id, request)
                    : undefined}
                />
              ))}
            </TableBody>
          </Table>
        );

      case 'failureModes':
        return (
          <Table className={tableMinWidthByType.failureModes}>
            <TableHeader>
              <TableRow className="hover:bg-transparent">
                <TableHead className="w-[50px]" />
                <TableHead className="revy-cjk w-[220px]">
                  {t('drbfm.table.failureDescription')}
                </TableHead>
                <TableHead className="revy-cjk w-[260px]">{t('drbfm.table.failureCause')}</TableHead>
                <TableHead className="revy-cjk w-[260px]">{t('drbfm.table.failureEffect')}</TableHead>
                <TableHead className="revy-cjk w-[110px]">{t('drbfm.table.severity')}</TableHead>
                <TableHead className="revy-cjk w-[80px] text-center">
                  {t('drbfm.table.severityScore')}
                </TableHead>
                <TableHead className="revy-cjk w-[80px] text-center">
                  {t('drbfm.table.rpnScore')}
                </TableHead>
                <TableHead className="w-[64px] text-center">3D</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {paginatedData.map((row) => (
                <FailureModeRow
                  key={(row as FailureMode).id}
                  row={row as FailureMode}
                  drbfmSessionId={drbfmSessionId}
                  selected={(row as FailureMode).id === selectedFailureModeId}
                  onSelect={onSelectFailureMode}
                />
              ))}
            </TableBody>
          </Table>
        );

      case 'countermeasures':
        return (
          <Table className={tableMinWidthByType.countermeasures}>
            <TableHeader>
              <TableRow className="hover:bg-transparent">
                <TableHead className="revy-cjk">{t('drbfm.table.countermeasureType')}</TableHead>
                <TableHead className="revy-cjk">{t('drbfm.table.description')}</TableHead>
                <TableHead className="revy-cjk text-center">
                  {t('drbfm.table.effectiveness')}
                </TableHead>
                <TableHead className="revy-cjk text-center">{t('drbfm.table.priority')}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {paginatedData.map((row) => (
                <CountermeasureRow
                  key={(row as Countermeasure).id}
                  row={row as Countermeasure}
                />
              ))}
            </TableBody>
          </Table>
        );

      default:
        return null;
    }
  };

  return (
    <div className="@container/table">
      <div className="overflow-x-auto rounded-xl border bg-card">{renderTable()}</div>
      <div className="px-1 py-3">
        <DataTablePagination table={table} pageSizes={PAGE_SIZES} />
      </div>
    </div>
  );
};

export default DRBFMTable;
